import { FrameRate, Track } from '@features/Clip/types/media';

import { useAuthToken } from '@features/authentication';
import { Box, Flex, Text } from '@radix-ui/themes';
import React from 'react';
import styles from './ScrubberPreview.module.css';

interface ScrubberPreviewProps {
    tracks: Track[];
    frame: number;
    frameRate: FrameRate;
    pointerOffset: number;
    aspectRatio: number;
}

const formatToTimecode = (frame: number, frameRate: FrameRate) => {
    const fps = frameRate.numerator / frameRate.denumerator;
    const hours = Math.floor(frame / (3600 * fps));
    const minutes = Math.floor((frame / (60 * fps)) % 60);
    const seconds = Math.floor((frame / fps) % 60);
    const frames = Math.floor(frame % fps);

    return [hours, minutes, seconds, frames]
        .map((time) => time.toString().padStart(2, '0'))
        .join(':');
};

export const ScrubberPreview = ({
    tracks,
    frame,
    frameRate,
    pointerOffset,
    aspectRatio = 16 / 9,
}: ScrubberPreviewProps) => {
    const token = useAuthToken();

    const track = React.useMemo(
        () => tracks.find((track) => frame >= track.start && frame < track.end),
        [tracks, frame],
    );

    const tileStyles = React.useMemo(() => {
        if (!track) return undefined;

        const index = Math.floor(
            (track.stitchCount * (frame - track.start)) /
                (track.end - track.start),
        );

        const posX =
            track.stitchX > 1
                ? ((index % track.stitchX) * 100) / (track.stitchX - 1)
                : 0;

        const posY =
            track.stitchY > 1
                ? (Math.floor(index / track.stitchX) * 100) /
                  (track.stitchY - 1)
                : 0;

        return {
            '--stitch-x': track.stitchX,
            '--stitch-y': track.stitchY,
            backgroundImage: `url(${track.src}?access_token=${token})`,
            backgroundPosition: `${posX.toFixed(2)}% ${posY.toFixed(2)}%`,
            backgroundSize: `${track.stitchX * 100}% ${track.stitchY * 100}%`,
            aspectRatio: aspectRatio,
        } as React.CSSProperties;
    }, [aspectRatio, frame, token, track]);

    return (
        <Flex direction={'column'} className={styles.previewContainer}>
            {track ? (
                <Box className={styles.mosaicTile} style={tileStyles} />
            ) : (
                <Box className={styles.invalidMosaicTile} p={'4'} asChild>
                    <Text align={'center'}>No preview available</Text>
                </Box>
            )}

            <Box p='1' className={styles.labelArea}>
                <Text size='1' weight='bold'>
                    {formatToTimecode(frame, frameRate)}
                </Text>
            </Box>

            <Box
                className={styles.previewIndicator}
                style={{
                    left: `${pointerOffset}px`,
                }}
            />
        </Flex>
    );
};
